import searchIcon from "@public/img/search-black.svg";
import SelectField from "./widgets/select-field";
import TextField from "./widgets/text-field";
import SwitchField from "./widgets/switch-field";
import React, { useContext, useEffect, useRef, useState } from "react";
import { AppContextType, WonmovContext } from "../context/app-context";
import chevronDownIcon from "@public/img/chevron-down.svg";
import { SearchParams, SearchSort, SearchType } from "../entry/local/movie";
import { useHistory } from "react-router-dom";
import "@/styles/search-filter.css";

const typeOptions = [
  { label: "نوع", value: "" },
  { label: "فیلم", value: "movie" },
  { label: "سریال", value: "series" },
  { label: "انیمیشن", value: "animation" },
  { label: "انیمه", value: "anime" },
];

const sortOptions = [
  { label: "مرتب سازی", value: "" },
  { label: "جدیدترین", value: "newest" },
  { label: "به‌روزترین", value: "latest" },
  { label: "پر بازدیدترین", value: "most-viewed" },
  { label: "امتیاز Imdb", value: "imdb-rating" },
  { label: "سال انتشار", value: "release-year" },
];

const imdbRatingOptions = [
  { label: "امتیاز Imdb", value: "" },
  { label: "بالاتر از 9.0", value: "9.0" },
  { label: "بالاتر از 8.0", value: "8.0" },
  { label: "بالاتر از 7.0", value: "7.0" },
  { label: "بالاتر از 6.0", value: "6.0" },
  { label: "بالاتر از 5.0", value: "5.0" },
];

type SearchFilterProps = {
  showAlwaysOnDesktop?: boolean;
};

function buildSearchQuery(params: SearchParams): string {
  var parts: string[] = [];
  var record = params as Record<string, string | number | boolean | undefined>;
  var key: string;

  for (key in record) {
    if (Object.prototype.hasOwnProperty.call(record, key)) {
      var value = record[key];
      if (value !== undefined && value !== null && value !== "") {
        parts.push(encodeURIComponent(key) + "=" + encodeURIComponent(String(value)));
      }
    }
  }

  return parts.join("&");
}

export default function SearchFilter({ showAlwaysOnDesktop = true }: SearchFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { searchFilters, isSearchFilterOpen, setIsSearchFilterOpen } = useContext<AppContextType>(WonmovContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  const [searchText, setSearchText] = useState<string>("");
  const [searchType, setSearchType] = useState<string>("");
  const [searchGenre, setSearchGenre] = useState<string>("0");
  const [searchSort, setSearchSort] = useState<string>("");
  const [searchImdbRating, setSearchImdbRating] = useState<string>("");
  const [searchDubbed, setSearchDubbed] = useState<boolean>(false);
  const [searchSubtitled, setSearchSubtitled] = useState<boolean>(false);
  const [searchActor, setSearchActor] = useState<string>("");
  const [searchDirector, setSearchDirector] = useState<string>("");
  const [searchYearFrom, setSearchYearFrom] = useState<string>("");
  const [searchYearTo, setSearchYearTo] = useState<string>("");
  const [searchNetwork, setSearchNetwork] = useState<string>("0");
  const [searchStatus, setSearchStatus] = useState<string>("0");
  const [searchLanguage, setSearchLanguage] = useState<string>("0");
  const [searchCountry, setSearchCountry] = useState<string>("0");
  const [searchAgeRating, setSearchAgeRating] = useState<string>("0");

  const showExpanded = isOpen || isSearchFilterOpen;

  useEffect(function () {
    function onWindowClick(e: MouseEvent) {
      var target = e.target || e.srcElement;
      if (!target || !containerRef.current) {
        return;
      }

      if (!containerRef.current.contains(target as Node)) {
        var element = target as HTMLElement;
        if (element.id !== "search-button") {
          setIsSearchFilterOpen(false);
        }
      }
    }

    window.addEventListener("click", onWindowClick);
    return function () {
      window.removeEventListener("click", onWindowClick);
    };
  }, [setIsSearchFilterOpen]);

  const handleSearch = function () {
    const searchParams: SearchParams = {};

    if (searchText.trim() !== "") searchParams.s = searchText.trim();
    if (searchType.trim() !== "") searchParams.type = searchType.trim() as SearchType;
    if (searchGenre.trim() !== "0") searchParams.genre = parseInt(searchGenre.trim(), 10);
    if (searchSort.trim() !== "") searchParams.sort = searchSort.trim() as SearchSort;
    if (searchImdbRating.trim() !== "") searchParams.imdb_rate = parseFloat(searchImdbRating.trim());
    if (searchDubbed) searchParams.is_dubbed = searchDubbed;
    if (searchSubtitled) searchParams.is_subbed = searchSubtitled;
    if (searchYearFrom.trim() !== "") searchParams.start_year = parseInt(searchYearFrom.trim(), 10);
    if (searchYearTo.trim() !== "") searchParams.end_year = parseInt(searchYearTo.trim(), 10);
    if (searchStatus.trim() !== "0") searchParams.status = searchStatus.trim();
    if (searchCountry.trim() !== "0") searchParams.country = parseInt(searchCountry.trim(), 10);
    if (searchLanguage.trim() !== "0") searchParams.language = parseInt(searchLanguage.trim(), 10);
    if (searchNetwork.trim() !== "0") searchParams.network = parseInt(searchNetwork.trim(), 10);
    if (searchDirector.trim() !== "") searchParams.director = searchDirector.trim();
    if (searchActor.trim() !== "") searchParams.actor = searchActor.trim();
    if (searchAgeRating.trim() !== "0") searchParams.age_rating = searchAgeRating.trim();

    if (checkSearchParams()) {
      setIsSearchFilterOpen(false);
      var query = buildSearchQuery(searchParams);
      history.push("/search" + (query ? "?" + query : ""));
    }
  };

  const checkSearchParams = function () {
    return searchText.trim() !== "" ||
      searchType !== "" ||
      searchGenre !== "0" ||
      searchSort !== "" ||
      searchImdbRating !== "" ||
      searchDubbed ||
      searchSubtitled ||
      searchYearFrom !== "" ||
      searchYearTo !== "" ||
      searchStatus !== "0" ||
      searchCountry !== "0" ||
      searchLanguage !== "0" ||
      searchNetwork !== "0" ||
      searchDirector !== "" ||
      searchActor !== "" ||
      searchAgeRating !== "0";
  };

  if (!searchFilters) {
    return (
      <div className="search-filter search-filter--loading">
        <div className="search-filter__panel shimmer" />
      </div>
    );
  }

  var sectionClass = "search-filter";
  if (isSearchFilterOpen) {
    sectionClass += " search-filter--open";
  } else {
    sectionClass += " search-filter--closed";
  }
  if (showAlwaysOnDesktop) {
    sectionClass += " search-filter--desktop-always";
  }

  var toggleIconClass = "search-filter__toggle-icon";
  if (showExpanded) {
    toggleIconClass += " search-filter__toggle-icon--open";
  }

  return (
    <section ref={containerRef} className={sectionClass} dir="rtl">
      <div className="search-filter__panel">
        <div className="search-filter__grid">
          {showExpanded ? (
            <div className="search-filter__item search-filter__item--full search-filter__item--mobile-first">
              <TextField
                placeholder="نام فیلم، سریال، کد IMDb و ..."
                autoComplete="off"
                onChange={function (e) { setSearchText(e.target.value); }}
                value={searchText}
              />
            </div>
          ) : null}

          <div className="search-filter__item">
            <SelectField uniqueKey="type" options={typeOptions} onChange={function (value) { setSearchType(value); }} value={searchType} />
          </div>
          <div className="search-filter__item">
            <SelectField uniqueKey="genre" options={searchFilters.genres} onChange={function (value) { setSearchGenre(value); }} value={searchGenre} />
          </div>
          <div className="search-filter__item">
            <SelectField uniqueKey="sort" options={sortOptions} onChange={function (value) { setSearchSort(value); }} value={searchSort} />
          </div>
          <div className="search-filter__item">
            <SelectField uniqueKey="imdb-rating" options={imdbRatingOptions} onChange={function (value) { setSearchImdbRating(value); }} value={searchImdbRating} />
          </div>

          <div className="search-filter__item">
            <button
              type="button"
              className="search-filter__toggle"
              onClick={function (e) {
                e.stopPropagation();
                setIsOpen(!isOpen);
                setIsSearchFilterOpen(false);
              }}
            >
              <span className="search-filter__toggle-label">{showExpanded ? "بستن" : "سایر فیلترها"}</span>
              <img src={chevronDownIcon} alt="" className={toggleIconClass} />
            </button>
          </div>

          {showExpanded ? (
            <div className="search-filter__item">
              <SelectField uniqueKey="age-rating" options={searchFilters.age_ratings} onChange={function (value) { setSearchAgeRating(value); }} value={searchAgeRating} />
            </div>
          ) : null}
          {showExpanded ? (
            <div className="search-filter__item">
              <SelectField uniqueKey="language" options={searchFilters.languages} onChange={function (value) { setSearchLanguage(value); }} value={searchLanguage} />
            </div>
          ) : null}
          {showExpanded ? (
            <div className="search-filter__item">
              <SelectField uniqueKey="country" options={searchFilters.countries} onChange={function (value) { setSearchCountry(value); }} value={searchCountry} />
            </div>
          ) : null}
          {showExpanded ? (
            <div className="search-filter__item">
              <SelectField uniqueKey="network" options={searchFilters.networks} onChange={function (value) { setSearchNetwork(value); }} value={searchNetwork} />
            </div>
          ) : null}
          {showExpanded ? (
            <div className="search-filter__item">
              <SelectField uniqueKey="status" options={searchFilters.statuses} onChange={function (value) { setSearchStatus(value); }} value={searchStatus} />
            </div>
          ) : null}
          {showExpanded ? (
            <div className="search-filter__item">
              <SwitchField label="دوبله فارسی" checked={searchDubbed} onChange={function (value) { setSearchDubbed(value); }} />
            </div>
          ) : null}
          {showExpanded ? (
            <div className="search-filter__item">
              <SwitchField label="زیرنویس فارسی" checked={searchSubtitled} onChange={function (value) { setSearchSubtitled(value); }} />
            </div>
          ) : null}
          {showExpanded ? (
            <div className="search-filter__item">
              <TextField placeholder="بازیگر" autoComplete="off" onChange={function (e) { setSearchActor(e.target.value); }} value={searchActor} />
            </div>
          ) : null}
          {showExpanded ? (
            <div className="search-filter__item">
              <TextField placeholder="کارگردان" autoComplete="off" onChange={function (e) { setSearchDirector(e.target.value); }} value={searchDirector} />
            </div>
          ) : null}
          {showExpanded ? (
            <div className="search-filter__item">
              <TextField placeholder="از سال" inputMode="numeric" autoComplete="off" onChange={function (e) { setSearchYearFrom(e.target.value); }} value={searchYearFrom} />
            </div>
          ) : null}
          {showExpanded ? (
            <div className="search-filter__item">
              <TextField placeholder="تا سال" inputMode="numeric" autoComplete="off" onChange={function (e) { setSearchYearTo(e.target.value); }} value={searchYearTo} />
            </div>
          ) : null}

          <div className="search-filter__item search-filter__item--submit">
            <button type="button" className="search-filter__submit" onClick={handleSearch}>
              <span className="search-filter__submit-label">جست و جو</span>
              <img src={searchIcon} alt="" className="search-filter__submit-icon" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
